import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { isMobile } from 'react-device-detect';

const noop = () => {};
const getFullscreenElement = () =>
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement;

const canRequestFullscreen = (element) =>
    !!(
        element &&
        (element.requestFullscreen ||
            element.webkitRequestFullscreen ||
            element.mozRequestFullScreen ||
            element.msRequestFullscreen)
    );

const requestFullscreenForElement = (element) => {
    if (!element) return;

    const requestMethod =
        element.requestFullscreen ||
        element.webkitRequestFullscreen ||
        element.mozRequestFullScreen ||
        element.msRequestFullscreen;

    if (!requestMethod) return;

    const result = requestMethod.call(element);
    if (result && typeof result.catch === 'function') {
        result.catch(noop);
    }
};

const exitFullscreenForDocument = () => {
    const exitMethod =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;

    if (exitMethod) {
        exitMethod.call(document);
    }
};

const FullscreenContext = React.createContext({
    isFullscreen: false,
    canFullscreen: false,
    requestFullscreen: noop,
    exitFullscreen: noop,
    requestFullscreenOnMobile: noop,
});

export const FullscreenProvider = ({ children }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [canFullscreenState, setCanFullscreenState] = useState(false);

    useEffect(() => {
        const updateStateFromDocument = () => {
            setIsFullscreen(!!getFullscreenElement());
            setCanFullscreenState(canRequestFullscreen(document.body));
        };

        // Initialize on mount
        updateStateFromDocument();

        // Listen for fullscreen changes
        document.addEventListener('fullscreenchange', updateStateFromDocument);
        document.addEventListener('webkitfullscreenchange', updateStateFromDocument);
        document.addEventListener('mozfullscreenchange', updateStateFromDocument);
        document.addEventListener('MSFullscreenChange', updateStateFromDocument);
        return () => {
            document.removeEventListener('fullscreenchange', updateStateFromDocument);
            document.removeEventListener('webkitfullscreenchange', updateStateFromDocument);
            document.removeEventListener('mozfullscreenchange', updateStateFromDocument);
            document.removeEventListener('MSFullscreenChange', updateStateFromDocument);
        };
    }, []);

    const requestFullscreen = useCallback(() => {
        if (getFullscreenElement()) {
            exitFullscreenForDocument();
            return;
        }

        requestFullscreenForElement(document.body);
    }, []);

    const exitFullscreen = useCallback(() => {
        if (getFullscreenElement()) {
            exitFullscreenForDocument();
        }
    }, []);

    const requestFullscreenOnMobile = useCallback(() => {
        if (isMobile && !getFullscreenElement()) {
            requestFullscreenForElement(document.body);
        }
    }, []);

    const value = useMemo(() => ({
        isFullscreen,
        canFullscreen: canFullscreenState,
        requestFullscreen,
        exitFullscreen,
        requestFullscreenOnMobile,
    }), [isFullscreen, canFullscreenState, requestFullscreen, exitFullscreen, requestFullscreenOnMobile]);

    return (
        <FullscreenContext.Provider value={value}>
            {children}
        </FullscreenContext.Provider>
    );
};

export const useFullscreen = () => {
    return useContext(FullscreenContext);
};
